import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  DocumentIcon,
  FolderIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  Squares2X2Icon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import ButtonComponent from '../customButton';
import TableComponent from '../TableComponent';
import RowActionMenu from '../menu/RowActionMenu';
import { getExecutionFileDownloadUrl, getExecutionFileUsage } from '../../utils/api/executionFileApis';
import { STATUS } from '../../utils/api/sharedfunction';
import { useSnackbar } from '../../utils/useSnackbar';
import { formatFileSize, formatDate, getDisplayFileName } from './helpers';
import FileTypeChip from './FileTypeChip';
import ExplorerView from './ExplorerView';
import EditFileModal from './EditFileModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import styles from '../../styles/assetsManagement/filesManager.module.scss';

const getInitials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

const UserCell = ({ user }) => {
  if (!user) return <span className={styles.descCell}>—</span>;
  return (
    <div className={styles.userCell}>
      <span className={styles.userAvatar} title={user.name}>{getInitials(user.name)}</span>
      <span className={styles.userName} title={user.name}>{user.name}</span>
    </div>
  );
};

const FileListPanel = ({ folder, files, loadingFiles, onUpload, onEditFile, onDeleteFile, onMoveFile, allFilesView }) => {
  const { showSnackbar } = useSnackbar();
  const [viewMode, setViewMode] = useState('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filePopup, setFilePopup] = useState(null);
  const [filePopupTarget, setFilePopupTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [usedInTestCases, setUsedInTestCases] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);

  const closeFilePopup = useCallback(() => {
    setFilePopup(null);
    setFilePopupTarget(null);
    setUsedInTestCases([]);
  }, []);

  const handleDeleteClick = useCallback(async (row) => {
    if (!row?.id) {
      showSnackbar('Cannot delete: file information is missing.', 'error');
      return;
    }
    setUsageLoading(true);
    try {
      const res = await getExecutionFileUsage({ executionFileId: row.id });
      if (res.status === STATUS.SUCCESS && res.data?.usedInTestCases?.length) {
        setUsedInTestCases(res.data.usedInTestCases);
      } else {
        setUsedInTestCases([]);
      }
    } catch {
      showSnackbar('Unexpected error', 'error');
      setUsedInTestCases([]);
    } finally {
      setUsageLoading(false);
    }
    setFilePopupTarget(row);
    setFilePopup('delete');
  }, [showSnackbar]);

  const filteredFiles = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return files;
    return files.filter(
      (f) =>
        getDisplayFileName(f).toLowerCase().includes(q) ||
        f.fileDescription?.toLowerCase().includes(q) ||
        f.folderName?.toLowerCase().includes(q) ||
        f.uploadedBy?.name?.toLowerCase().includes(q) ||
        f.updatedBy?.name?.toLowerCase().includes(q),
    );
  }, [files, searchQuery]);

  const handleDownload = useCallback(async (fileItem) => {
    try {
      const res = await getExecutionFileDownloadUrl({ executionFileId: fileItem.id });
      if (res.status !== STATUS.SUCCESS) {
        showSnackbar(res.error || 'Failed to get download URL', 'error');
        return;
      }
      const { downloadUrl, fileName: apiFileName } = res.data;
      const downloadFileName = apiFileName || getDisplayFileName(fileItem);
      const blob = await fetch(downloadUrl, { credentials: 'omit' }).then((r) => r.blob());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFileName;
      a.rel = 'noopener noreferrer';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showSnackbar('Unexpected error', 'error');
    }
  }, [showSnackbar]);

  const onMoveFileRef = useRef(onMoveFile);
  const handleDownloadRef = useRef(handleDownload);
  const handleDeleteClickRef = useRef(handleDeleteClick);
  onMoveFileRef.current = onMoveFile;
  handleDownloadRef.current = handleDownload;
  handleDeleteClickRef.current = handleDeleteClick;

  const rowActionButtons = useMemo(() => [
    {
      name: 'Download',
      tooltip: 'Download file',
      icon: <ArrowDownTrayIcon className="icon" />,
      action: (row) => handleDownloadRef.current(row),
      disabled: (row) => row?.scannedStatus !== 'CLEAN',
    },
    {
      name: 'Move',
      tooltip: 'Move file to another folder',
      icon: <ArrowPathIcon className="icon" />,
      action: (row) => onMoveFileRef.current?.(row),
      disabled: false,
    },
    {
      name: 'Edit',
      tooltip: 'Edit file details',
      icon: <PencilIcon className="icon" />,
      action: (row) => { setFilePopupTarget(row); setFilePopup('edit'); },
      disabled: false,
    },
    {
      name: 'Delete',
      tooltip: 'Delete file',
      icon: <TrashIcon className="icon" />,
      action: (row) => handleDeleteClickRef.current(row),
      disabled: false,
    },
  ], []);

  const showFolderColumn = !!allFilesView;

  const fileColumns = useMemo(() => [
    {
      field: 'fileName',
      headerName: 'File Name',
      flex: 2,
      minWidth: 160,
      valueGetter: (value, row) => getDisplayFileName(row),
      renderCell: (params) => {
        const displayName = getDisplayFileName(params.row);
        return (
          <div className={styles.fileNameCell}>
            <div className={styles.fileIconBox}><DocumentIcon /></div>
            <span className={styles.fileName} title={displayName}>{displayName}</span>
            {params.row.duplicate && (
              <span className={styles.dupBadge} title="Duplicate — linked to existing file">dup</span>
            )}
          </div>
        );
      },
    },
    ...(showFolderColumn ? [{
      field: 'folderName',
      headerName: 'Folder',
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <span className={styles.descCell} title={params.value || ''}>
          {params.value || '—'}
        </span>
      ),
    }] : []),
    {
      field: 'fileDescription',
      headerName: 'Description',
      flex: 2,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <span className={styles.descCell} title={params.value || ''}>
          {params.value || '—'}
        </span>
      ),
    },
    {
      field: 'scannedStatus',
      headerName: 'Status',
      width: 110,
      sortable: false,
      renderCell: (params) => {
        const status = params.value;
        const label =
          status === 'SCANNING' ? 'Scanning' :
          status === 'CLEAN' ? 'Clean' :
          status === 'INFECTED' ? 'Infected' :
          status === 'ERROR' ? 'Error' :
          '—';
        return <span className={styles.descCell}>{label}</span>;
      },
    },
    {
      field: 'fileType',
      headerName: 'Type',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <FileTypeChip mimeType={params.value} fileName={getDisplayFileName(params.row)} />
      ),
    },
    {
      field: 'fileSize',
      headerName: 'Size',
      width: 90,
      renderCell: (params) => <span>{formatFileSize(params.value)}</span>,
    },
    {
      field: 'uploadedAt',
      headerName: 'Uploaded',
      width: 120,
      renderCell: (params) => <span>{formatDate(params.value)}</span>,
    },
    {
      field: 'uploadedBy',
      headerName: 'Uploaded By',
      width: 160,
      sortable: false,
      renderCell: (params) => <UserCell user={params.value} />,
    },
    {
      field: 'updatedBy',
      headerName: 'Updated By',
      width: 160,
      sortable: false,
      renderCell: (params) => <UserCell user={params.value} />,
    },
    {
      field: 'Actions',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      width: 70,
      resizable: false,
      renderCell: (params) => (
        <RowActionMenu menuItems={rowActionButtons} rowData={params.row} />
      ),
    },
  ], [rowActionButtons, showFolderColumn]);

  const breadcrumbParts = folder?.folderPath?.split(' / ') || [];
  const hasFiles = files.length > 0;
  const noSearchResults = hasFiles && filteredFiles.length === 0;

  return (
    <div className={styles.filePanel}>
      <div className={styles.filePanelHeader}>
        <div className={styles.breadcrumb}>
          <FolderIcon className={styles.breadcrumbIcon} />
          {folder ? (
            breadcrumbParts.map((part, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className={styles.breadcrumbSep}>/</span>}
                <span className={`${styles.breadcrumbPart} ${i === breadcrumbParts.length - 1 ? styles.active : ''}`}>
                  {part}
                </span>
              </React.Fragment>
            ))
          ) : (
            <span className={`${styles.breadcrumbPart} ${styles.active}`}>All Files</span>
          )}
        </div>

        <div className={styles.filePanelActions}>
          {hasFiles && (
            <div className={styles.fileSearch}>
              <MagnifyingGlassIcon className={styles.fileSearchIcon} />
              <input
                type="text"
                className={styles.fileSearchInput}
                placeholder="Search files…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search files"
              />
              {searchQuery && (
                <button
                  type="button"
                  className={styles.fileSearchClear}
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <XMarkIcon />
                </button>
              )}
            </div>
          )}

          {hasFiles && (
            <div className={styles.viewToggle} role="group" aria-label="Change view">
              <button
                type="button"
                className={`${styles.viewToggleBtn} ${viewMode === 'list' ? styles.viewToggleActive : ''}`}
                onClick={() => setViewMode('list')}
                title="List view"
                aria-pressed={viewMode === 'list'}
                aria-label="List view"
              >
                <ListBulletIcon />
              </button>
              <button
                type="button"
                className={`${styles.viewToggleBtn} ${viewMode === 'explorer' ? styles.viewToggleActive : ''}`}
                onClick={() => setViewMode('explorer')}
                title="Explorer view"
                aria-pressed={viewMode === 'explorer'}
                aria-label="Explorer view"
              >
                <Squares2X2Icon />
              </button>
            </div>
          )}

          {folder && (
            <ButtonComponent
              ctaText="Upload"
              variant="primary"
              className="white bg-blue600"
              startIcon={<ArrowUpTrayIcon style={{ width: 15, height: 15 }} />}
              onClick={onUpload}
              size="small"
            />
          )}
        </div>
      </div>

      <div className={styles.filePanelBody}>
        {!folder && !allFilesView ? (
          <div className={styles.emptyState}>
            <FolderIcon className={styles.emptyIcon} />
            <p className={styles.emptyTitle}>Select a folder to view its files</p>
            <p className={styles.emptyHint}>Use the folder tree on the left to navigate</p>
          </div>

        ) : loadingFiles ? (
          <div className={styles.emptyState}>
            <span className={styles.spinner} aria-label="Loading files" />
            <p className={styles.emptyHint}>Loading files…</p>
          </div>

        ) : !hasFiles ? (
          <div className={styles.emptyState}>
            <DocumentIcon className={styles.emptyIcon} />
            <p className={styles.emptyTitle}>{allFilesView ? 'No files yet' : 'No files in this folder'}</p>
            <p className={styles.emptyHint}>{allFilesView ? 'Select a folder to upload files' : 'Upload your first execution file'}</p>
            {!allFilesView && (
              <ButtonComponent
                ctaText="Upload File"
                variant="primary"
                className="white bg-blue600"
                startIcon={<ArrowUpTrayIcon style={{ width: 15, height: 15 }} />}
                onClick={onUpload}
                size="small"
              />
            )}
          </div>

        ) : noSearchResults ? (
          <div className={styles.emptyState}>
            <MagnifyingGlassIcon className={styles.emptyIcon} />
            <p className={styles.emptyTitle}>No files match &ldquo;{searchQuery}&rdquo;</p>
            <p className={styles.emptyHint}>Try a different name, description, or author</p>
          </div>

        ) : viewMode === 'explorer' ? (
          <div className={styles.explorerWrap}>
            <ExplorerView files={filteredFiles} rowActionButtons={rowActionButtons} />
          </div>

        ) : (
          <TableComponent
            rows={filteredFiles}
            columns={fileColumns}
            idField="id"
            viewType="executionFiles"
            tableHeight="100%"
            isHeaderContentVisible={false}
            hideColumnsVisibilityButton
            disableColumnMenu
            disableFooter
          />
        )}
      </div>

      {filePopup === 'edit' && filePopupTarget && (
        <EditFileModal
          file={filePopupTarget}
          onSave={async (updates) => { await onEditFile(filePopupTarget.id, updates); closeFilePopup(); }}
          onClose={closeFilePopup}
        />
      )}

      {filePopup === 'delete' && filePopupTarget && (
        <DeleteConfirmModal
          title="Delete File"
          message={
            usedInTestCases.length > 0 ? (
              <>
                <strong>This file is present in the automation plan of the following test cases: {usedInTestCases.map((t) => t.key || t.id).filter(Boolean).join(', ')}.</strong>
                {' '}
                Delete &quot;{getDisplayFileName(filePopupTarget) || 'this file'}&quot;? This action cannot be undone.
              </>
            ) : (
              `Delete "${getDisplayFileName(filePopupTarget) || 'this file'}"? This action cannot be undone.`
            )
          }
          onConfirm={async () => { setDeleteLoading(true); await onDeleteFile(filePopupTarget.id); setDeleteLoading(false); closeFilePopup(); }}
          onClose={closeFilePopup}
          loading={deleteLoading}
        />
      )}
    </div>
  );
};

export default FileListPanel;
